import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const translations = {
  en: {
    onboardingTitle: "Let's start your onboarding",
    languageTitle: "What's your language?",
    educationTitle: "What's your education level?",
    goalTitle: "What is your goal?",
    finish: 'Finish & Create account',
    startLearning: 'Start Learning',
    nav: {
      courses: 'courses',
      help: 'help',
      about: 'about us',
      login: 'login',
      dashboard: 'Dashboard',
      profile: 'Profile',
      signOut: 'Sign Out'
    },
    goals: {
      improve: 'Improve grades',
      understand: 'Understand Better',
      prepare: 'Prepare for exams',
      review: 'Review past topics',
      curiosity: 'Curiosity / Fun'
    }
  },
  fr: {
    onboardingTitle: "Commençons votre intégration",
    languageTitle: "Quelle est votre langue ?",
    educationTitle: "Quel est votre niveau scolaire ?",
    goalTitle: "Quel est votre objectif ?",
    finish: 'Terminer et créer un compte',
    startLearning: "Commencer",
    nav: {
      courses: 'cours',
      help: 'aide',
      about: 'à propos',
      login: 'connexion',
      dashboard: 'Tableau de bord',
      profile: 'Profil',
      signOut: 'Se déconnecter'
    },
    goals: {
      improve: 'Améliorer les notes',
      understand: "Mieux comprendre",
      prepare: "Préparer les examens",
      review: 'Revoir les anciens sujets',
      curiosity: 'Curiosité / Plaisir'
    }
  },
  it: {
    onboardingTitle: "Iniziamo il tuo onboarding",
    languageTitle: "Qual è la tua lingua?",
    educationTitle: "Qual è il tuo livello scolastico?",
    goalTitle: "Qual è il tuo obiettivo?",
    finish: 'Termina e crea account',
    startLearning: 'Inizia',
    nav: {
      courses: 'corsi',
      help: 'aiuto',
      about: 'informazioni',
      login: 'accesso',
      dashboard: 'Dashboard',
      profile: 'Profilo',
      signOut: 'Disconnetti'
    },
    goals: {
      improve: 'Migliorare i voti',
      understand: 'Capire meglio',
      prepare: 'Prepararsi agli esami',
      review: 'Ripassare argomenti passati',
      curiosity: 'Curiosità / Divertimento'
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const { profile, user, updateProfile } = useAuth();

  useEffect(() => {
    if (profile?.language_preference && translations[profile.language_preference]) {
      setLanguage(profile.language_preference);
    }
  }, [profile]);

  const changeLanguage = useCallback(async (nextLanguage) => {
    if (!translations[nextLanguage]) {
      console.warn(`Unsupported language requested: ${nextLanguage}`);
      return;
    }

    setLanguage(nextLanguage);

    if (user) {
      try {
        await updateProfile({ language_preference: nextLanguage });
      } catch (err) {
        console.warn('Failed to persist language preference to Supabase', err);
      }
    }
  }, [updateProfile, user]);

  const t = (keyPath) => {
    const parts = keyPath.split('.');
    let obj = translations[language] || translations.en;
    for (let p of parts) {
      if (obj && Object.prototype.hasOwnProperty.call(obj, p)) obj = obj[p];
      else return keyPath;
    }
    return obj;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
